import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import moment from 'moment';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';

export interface IUpdateNextMeetingRequest {
	/** The account whose next CSM meeting is being set. */
	accountId: string;
	/** The next-meeting date (a `Date` or parseable date string); sent as MM/DD/YYYY. */
	nextMeeting: Date | string;
}

/**
 * Sets an account's next CSM meeting via `PUT admin/account/{id}/additionalInfo/nextMeeting`
 * (the react-query replacement for the MobX `AccountOperationsViewModel.updateNextMeeting`). The
 * date is passed as a `MM/DD/YYYY` query param with no body; the updated `Api.IAccount` is returned.
 * Mutations have no MutationCache, so the error toast is wired here explicitly.
 */
export function useUpdateNextMeetingMutation() {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({ accountId, nextMeeting }: IUpdateNextMeetingRequest) =>
			userSession.webServiceHelper.callAsync<Api.IAccount>(
				`admin/account/${accountId}/additionalInfo/nextMeeting?nextMeeting=${encodeURIComponent(
					moment(nextMeeting).format('MM/DD/YYYY')
				)}`,
				'PUT'
			),
		onError: () => toast.error('Failed to update the next meeting. Please try again.'),
	});
}
